import type { MisconceptionDefinition } from './types'

/** MET — Metriek misconcepties. */
export const MET_MISCONCEPTIONS: MisconceptionDefinition[] = [
  {
    id: 'met.unit-factor-10',
    label: 'Factor 10 fout',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt of deelt met de verkeerde factor van 10 bij eenheidsomrekening.',
    typicalFamilies: ['MET-LEN-OMREKEN', 'MET-GEW-OMREKEN', 'MET-INH-OMREKEN'],
  },
  // ─── Coordinaten ────────────────────────────────────────────────────────
  {
    id: 'met.coord-xy-omgedraaid',
    label: 'x en y omgedraaid',
    module: 'Metriek',
    description:
      'Leerling leest of schrijft eerst y en daarna x bij een coördinaat.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-y-als-x-gelezen',
    label: 'y-coördinaat als x gelezen',
    module: 'Metriek',
    description:
      'Leerling gebruikt het tweede getal alsof het de x-coördinaat is.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-x-als-y-gelezen',
    label: 'x-coördinaat als y gelezen',
    module: 'Metriek',
    description:
      'Leerling gebruikt het eerste getal alsof het de y-coördinaat is.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-translatie-x-vergeten',
    label: 'x-verandering vergeten bij verschuiving',
    module: 'Metriek',
    description:
      'Leerling verandert alleen de y-coördinaat bij een verschuiving en laat x gelijk.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-translatie-y-vergeten',
    label: 'y-verandering vergeten bij verschuiving',
    module: 'Metriek',
    description:
      'Leerling verandert alleen de x-coördinaat bij een verschuiving en laat y gelijk.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-translatie-aftrekken',
    label: 'Aftrekken bij verschuiving naar rechts/omhoog',
    module: 'Metriek',
    description:
      'Leerling trekt de verschuiving af in plaats van op te tellen bij naar rechts of omhoog.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-oorsprong-een-een',
    label: 'Oorsprong als (1,1)',
    module: 'Metriek',
    description: 'Leerling denkt dat het assenstelsel begint bij (1, 1).',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-oorsprong-op-as',
    label: 'Oorsprong op één as',
    module: 'Metriek',
    description:
      'Leerling kiest een punt op de x- of y-as (zoals (1,0) of (0,1)) als oorsprong.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-rechthoek-x-fout',
    label: 'Verkeerde x bij vierde hoekpunt',
    module: 'Metriek',
    description:
      'Leerling kiest een x-coördinaat die bij geen van de gegeven hoekpunten hoort.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-rechthoek-y-fout',
    label: 'Verkeerde y bij vierde hoekpunt',
    module: 'Metriek',
    description:
      'Leerling kiest een y-coördinaat die bij geen van de gegeven hoekpunten hoort.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-afstand-optellen',
    label: 'Coördinaten optellen i.p.v. aftrekken',
    module: 'Metriek',
    description:
      'Leerling telt y-coördinaten op voor de afstand i.p.v. ze van elkaar af te trekken.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-afstand-rekenfout',
    label: 'Rekenfout bij afstand',
    module: 'Metriek',
    description:
      'Leerling maakt een rekenfout bij het verschil tussen twee coördinaten.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  {
    id: 'met.coord-afstand-coordinaat-i.p.v.-verschil',
    label: 'Coördinaat i.p.v. afstand gegeven',
    module: 'Metriek',
    description:
      'Leerling geeft een van de coördinaten als afstand in plaats van het verschil.',
    typicalFamilies: ['MET-COORDINATEN'],
  },
  // ─── Cirkel: diameter / straal ──────────────────────────────────────────
  {
    id: 'met.straal-diameter-omgedraaid',
    label: 'Straal/diameter omgedraaid (×2 i.p.v. ÷2)',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt met 2 om de straal te krijgen, of deelt door 2 om de diameter te krijgen.',
    typicalFamilies: ['MET-MEETK-DIAMETER'],
  },
  {
    id: 'met.straal-gelijk-aan-diameter',
    label: 'Straal en diameter gelijk gezien',
    module: 'Metriek',
    description:
      'Leerling denkt dat straal en diameter dezelfde lengte hebben.',
    typicalFamilies: ['MET-MEETK-DIAMETER'],
  },
  {
    id: 'met.straal-diameter-deel-4',
    label: 'Diameter delen door 4',
    module: 'Metriek',
    description:
      'Leerling deelt de diameter door 4 in plaats van door 2 om de straal te krijgen.',
    typicalFamilies: ['MET-MEETK-DIAMETER'],
  },
  {
    id: 'met.diameter-keer-3',
    label: 'Diameter = straal × 3',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt de straal met 3 in plaats van met 2 voor de diameter.',
    typicalFamilies: ['MET-MEETK-DIAMETER'],
  },
  {
    id: 'met.cirkel-straal-langer',
    label: 'Denkt dat straal langer is dan diameter',
    module: 'Metriek',
    description:
      'Leerling denkt dat de straal langer is dan de diameter (begripsfout).',
    typicalFamilies: ['MET-MEETK-DIAMETER'],
  },
  {
    id: 'met.cirkel-een-straal',
    label: 'Denkt cirkel heeft maar één straal',
    module: 'Metriek',
    description:
      'Leerling denkt dat een cirkel maar één straal heeft (i.p.v. oneindig veel even lange).',
    typicalFamilies: ['MET-MEETK-DIAMETER'],
  },
  {
    id: 'met.cirkel-diameter-helft',
    label: 'Denkt dat diameter helft van straal is',
    module: 'Metriek',
    description:
      'Leerling draait de relatie om en denkt dat diameter de helft van de straal is.',
    typicalFamilies: ['MET-MEETK-DIAMETER'],
  },
  // ─── Cirkel: omtrek ─────────────────────────────────────────────────────
  {
    id: 'met.cirkel-omtrek-zonder-factor-2',
    label: 'Omtrek zonder factor 2 (πr)',
    module: 'Metriek',
    description:
      'Leerling rekent π × straal in plaats van 2 × π × straal voor de omtrek.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-omtrek-als-oppervlakte',
    label: 'Oppervlakteformule i.p.v. omtrekformule',
    module: 'Metriek',
    description:
      'Leerling gebruikt π × r² (oppervlakte) terwijl de vraag naar de omtrek is.',
    typicalFamilies: ['MET-MEETK-OMTREK', 'MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.cirkel-omtrek-diameter-i.p.v.-formule',
    label: 'Diameter als omtrek genomen',
    module: 'Metriek',
    description:
      'Leerling geeft de diameter als antwoord op de omtrek-vraag, zonder te rekenen.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-omtrek-2pi-keer-d',
    label: 'Omtrek = 2 × π × diameter (dubbele factor 2)',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt diameter met 2π in plaats van met π (dubbele factor 2).',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-omtrek-pi-keer-r',
    label: 'Omtrek = π × straal (factor 2 vergeten)',
    module: 'Metriek',
    description: 'Leerling rekent π × straal als omtrek, zonder de factor 2.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-omtrek-eenheid-fout',
    label: 'Verkeerde eenheid voor diameter gebruikt',
    module: 'Metriek',
    description:
      'Leerling gebruikt een verkeerd getal of eenheid (bijv. 100 i.p.v. 10) voor de diameter.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-straal-uit-omtrek-deel-pi',
    label: 'Straal = omtrek ÷ π (zonder factor 2)',
    module: 'Metriek',
    description:
      'Leerling deelt de omtrek alleen door π en krijgt zo de diameter, niet de straal.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-straal-uit-omtrek-deel-vier-pi',
    label: 'Straal = omtrek ÷ 4π (extra factor 2)',
    module: 'Metriek',
    description:
      'Leerling deelt de omtrek door 4π in plaats van 2π bij het terugrekenen.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-straal-uit-omtrek-deel-2',
    label: 'Straal = omtrek ÷ 2 (π vergeten)',
    module: 'Metriek',
    description:
      'Leerling deelt de omtrek alleen door 2 en vergeet door π te delen.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-diameter-uit-omtrek-deel-2',
    label: 'Diameter = omtrek ÷ 2 (π vergeten)',
    module: 'Metriek',
    description:
      'Leerling deelt de omtrek door 2 en krijgt zo half de omtrek i.p.v. de diameter.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-diameter-uit-omtrek-deel-fout',
    label: 'Diameter uit omtrek met verkeerde deler',
    module: 'Metriek',
    description:
      'Leerling deelt de omtrek door een verkeerde factor (anders dan π) bij het terugrekenen naar diameter.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  {
    id: 'met.cirkel-straal-i.p.v.-diameter-in-formule',
    label: 'Straal i.p.v. diameter in formule',
    module: 'Metriek',
    description:
      'Leerling vult de straal in waar de diameter hoort (of andersom) bij omtrekberekening.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },
  // ─── Cirkel: oppervlakte ────────────────────────────────────────────────
  {
    id: 'met.cirkel-opp-als-omtrek',
    label: 'Omtrekformule i.p.v. oppervlakteformule',
    module: 'Metriek',
    description:
      'Leerling gebruikt 2πr (omtrek) terwijl de vraag naar de oppervlakte is.',
    typicalFamilies: ['MET-MEETK-OPP', 'MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.cirkel-opp-zonder-kwadraat',
    label: 'Straal niet gekwadrateerd',
    module: 'Metriek',
    description:
      'Leerling rekent π × straal in plaats van π × straal² bij de oppervlakte.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-opp-diameter-kwadraat',
    label: 'Diameter gekwadrateerd i.p.v. straal',
    module: 'Metriek',
    description:
      'Leerling kwadrateert de diameter in plaats van eerst de straal te berekenen.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-opp-diameter-zonder-pi',
    label: 'Diameter² zonder π',
    module: 'Metriek',
    description:
      'Leerling kwadrateert de diameter en vergeet π in de oppervlakteformule.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-opp-vergeet-wortel',
    label: 'Wortel vergeten bij terugrekenen',
    module: 'Metriek',
    description:
      'Leerling rekent oppervlakte ÷ π = straal² maar vergeet de wortel te trekken.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-opp-deel-2pi',
    label: 'Oppervlakte ÷ 2π i.p.v. ÷ π',
    module: 'Metriek',
    description:
      'Leerling deelt de oppervlakte door 2π in plaats van door π bij terugrekenen.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-opp-diameter-i.p.v.-straal',
    label: 'Diameter geleverd i.p.v. straal',
    module: 'Metriek',
    description:
      'Leerling geeft de diameter terwijl de vraag (terug)rekent naar de straal.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-halve-vergeet-halveren',
    label: 'Hele oppervlakte geleverd i.p.v. halve',
    module: 'Metriek',
    description:
      'Leerling berekent de hele oppervlakte maar vergeet te halveren bij een halve cirkel.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-halve-omtrek-i.p.v.-opp',
    label: 'Halve omtrek i.p.v. halve oppervlakte',
    module: 'Metriek',
    description:
      'Leerling berekent (halve) omtrek bij een vraag naar (halve) oppervlakte.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-halve-zonder-pi',
    label: 'r² zonder π en halvering',
    module: 'Metriek',
    description:
      'Leerling kwadrateert alleen de straal (of diameter) en vergeet π én halveren.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-ring-buiten-i.p.v.-verschil',
    label: 'Buitenste cirkel als ring',
    module: 'Metriek',
    description:
      'Leerling geeft de oppervlakte van de buitenste cirkel als antwoord op de ring.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-ring-binnen-i.p.v.-verschil',
    label: 'Binnenste cirkel als ring',
    module: 'Metriek',
    description:
      'Leerling geeft de oppervlakte van de binnenste cirkel als antwoord op de ring.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  {
    id: 'met.cirkel-ring-formule-fout',
    label: 'Verkeerde aanpak bij ring',
    module: 'Metriek',
    description:
      'Leerling gebruikt verschil van stralen of een omtrekformule i.p.v. verschil van oppervlaktes.',
    typicalFamilies: ['MET-MEETK-OPP'],
  },
  // ─── Cirkel: sectoren / cirkeldelen ─────────────────────────────────────
  {
    id: 'met.sector-deel-door-rest',
    label: 'Sectorhoek gedeeld door rest i.p.v. 360°',
    module: 'Metriek',
    description:
      'Leerling deelt de sectorhoek door (360 − hoek) in plaats van door 360.',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-deel-door-180',
    label: 'Sectorhoek gedeeld door 180°',
    module: 'Metriek',
    description:
      'Leerling deelt de sectorhoek door 180° (halve cirkel) i.p.v. door 360°.',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-deel-90-helft',
    label: 'Denkt 90° = halve cirkel',
    module: 'Metriek',
    description:
      'Leerling denkt dat 90° de helft van een cirkel is i.p.v. een kwart.',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-aantal-rekenfout',
    label: 'Rekenfout bij aantal sectoren',
    module: 'Metriek',
    description:
      'Leerling maakt een rekenfout bij 360° ÷ sectorhoek (bijv. 6 i.p.v. 8).',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-aantal-afronden-dichtst',
    label: 'Afronden naar dichtstbijzijnde aantal',
    module: 'Metriek',
    description:
      'Leerling rondt het aantal sectoren af terwijl 360° ÷ hoek exact moet opgaan.',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-aantal-afronden-boven',
    label: 'Aantal sectoren naar boven afgerond',
    module: 'Metriek',
    description:
      'Leerling rondt naar boven af bij het aantal sectoren in een cirkel.',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-boog-oppervlakteformule',
    label: 'Booglengte met oppervlakteformule (πr²)',
    module: 'Metriek',
    description:
      'Leerling gebruikt πr² voor booglengte i.p.v. 2πr (lengte vs. vlak).',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-correct-twijfel',
    label: 'Correcte berekening onterecht afgewezen',
    module: 'Metriek',
    description:
      'Leerling denkt dat een correcte sectorberekening fout is en kiest een verandering.',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-deel-360-discutabel',
    label: 'Twijfelt over delen door 360',
    module: 'Metriek',
    description:
      'Leerling denkt dat de deling door 360 in de sectorformule fout is.',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  {
    id: 'met.sector-factor-twee-twijfel',
    label: 'Twijfelt over factor 2 in formule',
    module: 'Metriek',
    description:
      'Leerling denkt dat een factor 2 mist in de sectorberekening, terwijl de formule klopt.',
    typicalFamilies: ['MET-MEETK-CIRKEL-DEEL'],
  },
  // ─── Hoeken ─────────────────────────────────────────────────────────────
  {
    id: 'met.hoek-som-190',
    label: 'Denkt dat driehoeksom 190° is',
    module: 'Metriek',
    description:
      'Leerling denkt dat de hoeken van een driehoek samen 190° zijn in plaats van 180°.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.hoek-verschil-ipv-aftrekken',
    label: 'Trekt hoeken van elkaar af i.p.v. van 180°',
    module: 'Metriek',
    description:
      'Leerling trekt de twee bekende hoeken van elkaar af in plaats van beide van 180° af te trekken.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.hoek-optellen-ipv-aftrekken',
    label: 'Telt bekende hoeken op als antwoord',
    module: 'Metriek',
    description:
      'Leerling telt de twee bekende hoeken op en geeft die som als de derde hoek.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.gelijkbenig-gelijkzijdig-verward',
    label: 'Verwart gelijkbenig met gelijkzijdig (60°)',
    module: 'Metriek',
    description:
      'Leerling denkt dat alle hoeken in een gelijkbenige driehoek 60° zijn (verwart met gelijkzijdig).',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.gelijkbenig-niet-delen',
    label: 'Vergeet door 2 te delen bij gelijkbenig',
    module: 'Metriek',
    description:
      'Leerling trekt de tophoek van 180° af maar vergeet het resultaat door 2 te delen voor de twee basishoeken.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.gelijkbenig-tophoek-gelijk',
    label: 'Denkt alle hoeken gelijk aan tophoek',
    module: 'Metriek',
    description:
      'Leerling denkt dat de basishoeken gelijk zijn aan de tophoek in een gelijkbenige driehoek.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.gestrekte-hoek-190',
    label: 'Denkt gestrekte hoek is 190°',
    module: 'Metriek',
    description:
      'Leerling denkt dat een gestrekte hoek (rechte lijn) 190° is in plaats van 180°.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.gestrekte-hoek-160',
    label: 'Denkt gestrekte hoek is 160°',
    module: 'Metriek',
    description:
      'Leerling denkt dat een gestrekte hoek 160° is in plaats van 180°.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.gestrekte-hoek-gelijk',
    label: 'Denkt hoeken op lijn altijd gelijk',
    module: 'Metriek',
    description:
      'Leerling denkt dat twee hoeken op een rechte lijn altijd even groot zijn.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.vierhoek-som-fout',
    label: 'Rekenfout bij optellen drie hoeken',
    module: 'Metriek',
    description:
      'Leerling maakt een rekenfout bij het optellen van de drie bekende hoeken in een vierhoek.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.vierhoek-aanname-90',
    label: 'Neemt aan dat vierde hoek 90° is',
    module: 'Metriek',
    description:
      'Leerling neemt aan dat de vierde hoek van een vierhoek 90° is zonder te rekenen.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.vierhoek-driehoek-verward',
    label: 'Gebruikt 180° i.p.v. 360° voor vierhoek',
    module: 'Metriek',
    description:
      'Leerling gebruikt de driehoeksom (180°) voor een vierhoek in plaats van 360°.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.angle-type-confused',
    label: 'Hoeksoort verward',
    module: 'Metriek',
    description:
      'Leerling verwart scherpe, stompe, rechte of gestrekte hoeken (bijv. 55° als stomp bestempeld).',
    typicalFamilies: ['MET-HOEK-SOORTEN'],
  },
  {
    id: 'met.angle-rotation-confused',
    label: 'Draaihoek verward',
    module: 'Metriek',
    description:
      'Leerling verwart kwart/halve/hele draai met het verkeerde aantal graden (bijv. kwart draai = 180°).',
    typicalFamilies: ['MET-HOEK-SOORTEN', 'MET-HOEK-BEREKENEN'],
  },
  // ─── Schaal & symmetrie ─────────────────────────────────────────────────
  {
    id: 'met.scale-unit-conversion',
    label: 'Eenheid vergeten om te rekenen bij schaal',
    module: 'Metriek',
    description:
      'Leerling vergeet cm naar m of km om te rekenen na de schaalberekening.',
    typicalFamilies: ['MET-SCHAAL-BASIS', 'MET-SCHAAL-LENGTE'],
  },
  {
    id: 'met.scale-area-not-squared',
    label: 'Schaalfactor niet gekwadrateerd bij oppervlakte',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt oppervlakte met de schaalfactor i.p.v. met het kwadraat ervan. Bijv. ×100 i.p.v. ×10.000 bij schaal 1:100.',
    typicalFamilies: ['MET-SCHAAL-OPP'],
  },
  {
    id: 'met.symmetry-line-count',
    label: 'Aantal symmetrielijnen verward',
    module: 'Metriek',
    description:
      'Leerling verwart het aantal symmetrielijnen van verschillende figuren (bijv. vierkant=4, rechthoek=2, gelijkzijdige driehoek=3).',
    typicalFamilies: ['MET-SYMMETRIE'],
  },
  {
    id: 'met.symmetry-mirror-axis',
    label: 'Spiegelas verward',
    module: 'Metriek',
    description:
      'Leerling spiegelt over de verkeerde as (bijv. horizontaal i.p.v. verticaal) of verwart x- en y-coördinaat bij spiegeling.',
    typicalFamilies: ['MET-SYMMETRIE'],
  },
  // ─── Inhoud (conversion) ────────────────────────────────────────────────
  {
    id: 'met.inhoud-trede-aantal-fout',
    label: 'Verkeerd aantal treden gebruikt',
    module: 'Metriek',
    description:
      'Leerling gebruikt het verkeerde aantal treden op de metrieke trap (bijv. 2 i.p.v. 3 treden tussen liter en milliliter).',
    typicalFamilies: ['MET-INH-LITER', 'MET-INH-CITER', 'MET-INH-OMREKEN'],
  },
  {
    id: 'met.inhoud-eenheid-verward',
    label: 'Inhoudseenheid verward',
    module: 'Metriek',
    description:
      'Leerling verwart twee inhoudseenheden (bijv. ml en dl als gelijk gezien, of liter ≈ deciliter).',
    typicalFamilies: ['MET-INH-LITER', 'MET-INH-CITER', 'MET-INH-OMREKEN'],
  },
  {
    id: 'met.inhoud-cm3-direct-als-liter',
    label: 'cm³ direct als liter genomen',
    module: 'Metriek',
    description:
      'Leerling beschouwt het aantal cm³ direct als aantal liters (vergeet ÷1.000) bij omrekening van blokinhoud naar liter.',
    typicalFamilies: ['MET-INH-LITER', 'MET-INH-BLOK', 'MET-INH-OMREKEN'],
  },

  // ─── Kubieke inhoud (omrekenen) ─────────────────────────────────────────
  {
    id: 'met.kubiek-x10-i.p.v.-x1000',
    label: 'Kubieke trap met ×10 i.p.v. ×1000',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt of deelt met factor 10 per trede bij kubieke maten (m³ → dm³) in plaats van factor 1.000.',
    typicalFamilies: ['MET-INH-OMREKEN-KUBIEK', 'MET-INH-OMREKEN'],
  },
  {
    id: 'met.kubiek-x100-i.p.v.-x1000',
    label: 'Kubieke trap met ×100 i.p.v. ×1000',
    module: 'Metriek',
    description:
      'Leerling gebruikt factor 100 per trede bij kubieke maten (verwart met oppervlakte) in plaats van 1.000.',
    typicalFamilies: ['MET-INH-OMREKEN-KUBIEK', 'MET-INH-OMREKEN'],
  },
  {
    id: 'met.kubiek-delen-i.p.v.-vermenigvuldigen',
    label: 'Delen i.p.v. vermenigvuldigen bij kubiek',
    module: 'Metriek',
    description:
      'Leerling deelt bij omrekening naar een kleinere kubieke eenheid in plaats van te vermenigvuldigen (richting omgedraaid).',
    typicalFamilies: ['MET-INH-OMREKEN-KUBIEK', 'MET-INH-OMREKEN'],
  },

  // ─── Oppervlakte omrekenen (m², ha, km²) ────────────────────────────────
  {
    id: 'met.area-step-factor-10',
    label: 'Oppervlakte met factor 10 i.p.v. 100',
    module: 'Metriek',
    description:
      'Leerling gebruikt factor 10 per trede bij oppervlakte-omrekening (cm² → dm²) in plaats van factor 100 (kwadraat van 10).',
    typicalFamilies: ['MET-OPP-OMREKENEN', 'MET-OPP-LAND'],
  },
  {
    id: 'met.area-step-factor-1000',
    label: 'Oppervlakte met factor 1000 i.p.v. 100',
    module: 'Metriek',
    description:
      'Leerling gebruikt factor 1.000 per trede bij oppervlakte-omrekening (verwart met kubieke maten) in plaats van factor 100.',
    typicalFamilies: ['MET-OPP-OMREKENEN', 'MET-OPP-LAND'],
  },
  {
    id: 'met.area-direction-reversed',
    label: 'Richting omgedraaid bij oppervlakte-omrekening',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt waar gedeeld moet worden (of andersom) bij oppervlakte-omrekening — vergelijkbaar met lineaire trapfout.',
    typicalFamilies: ['MET-OPP-OMREKENEN', 'MET-OPP-LAND'],
  },
  {
    id: 'met.opp-hectare-1000',
    label: 'Hectare als 1.000 m²',
    module: 'Metriek',
    description:
      'Leerling denkt dat 1 hectare = 1.000 m² (verwart met km of liter) in plaats van 10.000 m².',
    typicalFamilies: ['MET-OPP-LAND'],
  },
  {
    id: 'met.opp-hectare-100000',
    label: 'Hectare als 100.000 m²',
    module: 'Metriek',
    description:
      'Leerling denkt dat 1 hectare = 100.000 m² (een trede te ver) in plaats van 10.000 m².',
    typicalFamilies: ['MET-OPP-LAND'],
  },
  {
    id: 'met.opp-richting-fout',
    label: 'Richting fout bij ha/m²/km²',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt of deelt in de verkeerde richting tussen m², hectare en km².',
    typicalFamilies: ['MET-OPP-LAND', 'MET-OPP-OMREKENEN'],
  },

  // ─── Schaal — algemene richtingsfout ────────────────────────────────────
  {
    id: 'met.scale-direction',
    label: 'Schaal-richting omgedraaid',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt waar gedeeld moet worden (of andersom) bij schaalberekening (werkelijk ↔ kaart richting verwisseld).',
    typicalFamilies: [
      'MET-SCHAAL-BASIS',
      'MET-SCHAAL-LENGTE',
      'MET-SCHAAL-OPP',
    ],
  },

  // ─── Cirkel-omtrek (formule-substitutie) ────────────────────────────────
  {
    id: 'met.cirkel-straal-i.p.v.-diameter-formule',
    label: 'Straal i.p.v. diameter in omtrekformule',
    module: 'Metriek',
    description:
      'Leerling vult de straal in waar de diameter hoort in de omtrekformule (of omgekeerd), wat leidt tot een factor 2 fout. Alias van cirkel-straal-i.p.v.-diameter-in-formule.',
    typicalFamilies: ['MET-MEETK-OMTREK'],
  },

  // ─── Oppervlakte-omrekenen (stap-telfouten) ─────────────────────────────
  {
    id: 'met.area-step-undercount',
    label: 'Te weinig stappen op de trap',
    module: 'Metriek',
    description:
      'Leerling neemt te weinig stappen op de oppervlakte-trap (bijv. één stap i.p.v. twee), waardoor de omrekenfactor te klein is.',
    typicalFamilies: ['MET-OPP-OMREKENEN', 'MET-OPP-LAND'],
  },
  {
    id: 'met.area-step-overcount',
    label: 'Te veel stappen op de trap',
    module: 'Metriek',
    description:
      'Leerling neemt te veel stappen op de oppervlakte-trap, waardoor de omrekenfactor te groot wordt (bijv. ÷100.000 i.p.v. ÷10.000).',
    typicalFamilies: ['MET-OPP-OMREKENEN', 'MET-OPP-LAND'],
  },

  // ─── Driehoek-classificatie (zijden/hoeken) ─────────────────────────────
  {
    id: 'met.triangle-isosceles-vs-equilateral',
    label: 'Gelijkbenig vs. gelijkzijdig verward',
    module: 'Metriek',
    description:
      'Leerling verwart gelijkbenig (2 gelijke zijden) met gelijkzijdig (3 gelijke zijden), of denkt dat 2 gelijke zijden volstaan voor gelijkzijdig.',
    typicalFamilies: ['MET-DRIEHOEK-SOORTEN'],
  },
  {
    id: 'met.triangle-confuses-side-with-angle',
    label: 'Zijden-classificatie verward met hoeken-classificatie',
    module: 'Metriek',
    description:
      'Leerling gebruikt zijden-eigenschappen (gelijkbenig/gelijkzijdig) wanneer hoeken-classificatie (scherp/recht/stomp) gevraagd is, of andersom.',
    typicalFamilies: ['MET-DRIEHOEK-SOORTEN'],
  },
  {
    id: 'met.triangle-confuses-angle-with-side',
    label: 'Hoeken-classificatie verward met zijden-classificatie',
    module: 'Metriek',
    description:
      'Leerling gebruikt hoeken-eigenschappen waar zijden-classificatie gevraagd is, of verwart de twee classificatie-assen bij driehoeken.',
    typicalFamilies: ['MET-DRIEHOEK-SOORTEN'],
  },
  {
    id: 'met.triangle-opposite-classification',
    label: 'Tegengestelde driehoek-classificatie',
    module: 'Metriek',
    description:
      'Leerling kiest de tegenovergestelde classificatie (bijv. stomp i.p.v. scherp) — duidt op oppervlakkige toepassing van regels zonder visuele controle.',
    typicalFamilies: ['MET-DRIEHOEK-SOORTEN'],
  },
  {
    id: 'met.triangle-overlooks-equal-sides',
    label: 'Gelijke zijden over het hoofd gezien',
    module: 'Metriek',
    description:
      'Leerling herkent twee of drie gelijke zijden niet in de figuur en classificeert daardoor de driehoek als ongelijkzijdig.',
    typicalFamilies: ['MET-DRIEHOEK-SOORTEN'],
  },
  {
    id: 'met.triangle-angle-sum-unaware',
    label: '180°-regel niet toegepast',
    module: 'Metriek',
    description:
      'Leerling weet niet of past niet toe dat de som van de hoeken in een driehoek 180° is, en kan daardoor onmogelijke combinaties niet uitsluiten.',
    typicalFamilies: ['MET-DRIEHOEK-SOORTEN', 'MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.triangle-impossible-combination',
    label: 'Onmogelijke driehoek-combinatie gekozen',
    module: 'Metriek',
    description:
      'Leerling kiest een combinatie van zijden of hoeken die geen geldige driehoek vormt (bijv. hoekensom ≠ 180°, of driehoeksongelijkheid geschonden).',
    typicalFamilies: ['MET-DRIEHOEK-SOORTEN'],
  },
  {
    id: 'met.angle-right-vs-obtuse',
    label: 'Rechte hoek verward met stompe hoek',
    module: 'Metriek',
    description:
      'Leerling beschouwt 90° als stomp of een stompe hoek als recht; de drempel bij precies 90° wordt verkeerd geplaatst.',
    typicalFamilies: ['MET-DRIEHOEK-SOORTEN', 'MET-HOEK-SOORTEN'],
  },

  // ─── Gelijkbenige driehoek — hoekberekening varianten ───────────────────
  {
    id: 'met.gelijkbenig-niet-delen-context',
    label: 'Vergeet door 2 te delen (context)',
    module: 'Metriek',
    description:
      'Leerling vergeet in een contextopgave de overgebleven hoek door 2 te delen om de twee gelijke basishoeken van een gelijkbenige driehoek te krijgen.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.gelijkbenig-delen-door-3',
    label: 'Deelt tophoek door 3 i.p.v. rest door 2',
    module: 'Metriek',
    description:
      'Leerling deelt de tophoek door 3 (alsof alle hoeken gelijk zijn) i.p.v. 180° − tophoek door 2 te delen voor de basishoeken.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },
  {
    id: 'met.gelijkbenig-delen-door-6',
    label: 'Deelt tophoek door 6',
    module: 'Metriek',
    description:
      'Leerling deelt de tophoek door 6 — willekeurige delerfout zonder didactische logica, vaak door verkeerd onthouden regel.',
    typicalFamilies: ['MET-HOEK-BEREKENEN'],
  },

  // ─── Parallellogram-oppervlakte ─────────────────────────────────────────
  {
    id: 'met.parallelogram-slant-vs-height',
    label: 'Schuine zijde i.p.v. hoogte gebruikt',
    module: 'Metriek',
    description:
      'Leerling gebruikt de schuine zijde van het parallellogram als hoogte in de oppervlakteformule i.p.v. de loodrechte hoogte.',
    typicalFamilies: ['MET-OPP-PARALLELLOGRAM'],
  },
  {
    id: 'met.parallelogram-triangle-formula',
    label: 'Driehoekformule op parallellogram',
    module: 'Metriek',
    description:
      'Leerling past de driehoek-formule (basis × hoogte ÷ 2) toe op een parallellogram, terwijl de parallellogram-formule géén deling door 2 heeft.',
    typicalFamilies: ['MET-OPP-PARALLELLOGRAM'],
  },
  {
    id: 'met.parallelogram-uses-all-sides',
    label: 'Gebruikt alle zijden van parallellogram',
    module: 'Metriek',
    description:
      'Leerling denkt dat alle vier de zijden nodig zijn voor de oppervlakte van een parallellogram, i.p.v. alleen basis en hoogte.',
    typicalFamilies: ['MET-OPP-PARALLELLOGRAM'],
  },
  {
    id: 'met.parallelogram-conceptual-misconception',
    label: 'Onbegrepen reden parallellogram-formule',
    module: 'Metriek',
    description:
      'Leerling kan de formule basis × hoogte voor parallellogram niet conceptueel onderbouwen (waarom géén ÷ 2, of waarom hoogte i.p.v. zijde).',
    typicalFamilies: ['MET-OPP-PARALLELLOGRAM'],
  },

  // ─── Oppervlakte algemeen — driehoek + generalisatie ────────────────────
  {
    id: 'met.area-add-instead-multiply',
    label: 'Optellen i.p.v. vermenigvuldigen',
    module: 'Metriek',
    description:
      'Leerling telt de afmetingen op i.p.v. te vermenigvuldigen — verwart oppervlakte met omtrek of past de verkeerde bewerking toe.',
    typicalFamilies: [
      'MET-OPP-DRIEHOEK',
      'MET-OPP-PARALLELLOGRAM',
      'MET-OPP-VIERKANT',
      'MET-OPP-RECHT',
    ],
  },
  {
    id: 'met.area-forgot-halve',
    label: 'Vergeten te delen door 2 (driehoek)',
    module: 'Metriek',
    description:
      'Leerling berekent basis × hoogte voor een driehoek maar vergeet door 2 te delen, en krijgt zo de oppervlakte van een rechthoek.',
    typicalFamilies: ['MET-OPP-DRIEHOEK'],
  },
  {
    id: 'met.area-wrong-dimension',
    label: 'Verkeerde afmeting gebruikt',
    module: 'Metriek',
    description:
      'Leerling gebruikt een verkeerde afmeting in de oppervlakteformule (bijv. een schuine zijde, omtrek-deel, of niet-passende lijn).',
    typicalFamilies: [
      'MET-OPP-DRIEHOEK',
      'MET-OPP-PARALLELLOGRAM',
      'MET-OPP-VIERKANT',
    ],
  },
  {
    id: 'met.area-double-halve',
    label: 'Twee keer door 2 gedeeld',
    module: 'Metriek',
    description:
      'Leerling deelt twee keer door 2 bij een driehoek-berekening — overgeneralisatie van de ÷2-regel.',
    typicalFamilies: ['MET-OPP-DRIEHOEK'],
  },
  {
    id: 'met.area-double-both-dimensions',
    label: 'Beide afmetingen verdubbeld',
    module: 'Metriek',
    description:
      'Leerling denkt dat bij verdubbeling van één afmeting beide afmetingen verdubbelen, of verdubbelt de oppervlakte twee keer.',
    typicalFamilies: ['MET-OPP-DRIEHOEK', 'MET-OPP-PARALLELLOGRAM'],
  },
  {
    id: 'met.area-wrong-ratio',
    label: 'Verkeerde verhouding bij schaling',
    module: 'Metriek',
    description:
      'Leerling past de verkeerde verhouding toe bij vergroten/verkleinen van een oppervlakte (bijv. ×2 i.p.v. ×4 bij verdubbeling van zijde).',
    typicalFamilies: ['MET-OPP-DRIEHOEK', 'MET-SCHAAL-OPP'],
  },
  {
    id: 'met.area-perimeter-confusion',
    label: 'Oppervlakte met omtrek verward',
    module: 'Metriek',
    description:
      'Leerling rekent omtrek uit waar oppervlakte gevraagd is (of omgekeerd) — verwart de twee maten.',
    typicalFamilies: [
      'MET-OPP-PARALLELLOGRAM',
      'MET-OPP-VIERKANT',
      'MET-OPP-RECHT',
      'MET-OMTREK-RECHT',
    ],
  },
  {
    id: 'met.area-formula-overgeneralization',
    label: 'Oppervlakte-formule overgegeneraliseerd',
    module: 'Metriek',
    description:
      'Leerling past één oppervlakte-formule toe op alle vierhoeken zonder onderscheid (bijv. ÷ 2 voor rechthoek, of basis×hoogte voor trapezium).',
    typicalFamilies: ['MET-OPP-PARALLELLOGRAM', 'MET-OPP-SAMENGESTELD'],
  },
  {
    id: 'met.area-rule-misunderstanding',
    label: 'Oppervlakte-omrekenregel niet begrepen',
    module: 'Metriek',
    description:
      'Leerling past de ×100-regel mechanisch toe zonder te begrijpen dat oppervlakte twee dimensies omrekent — verwart "twee keer omrekenen" met "twee dimensies".',
    typicalFamilies: ['MET-OPP-OMREKENEN'],
  },
  {
    id: 'met.area-calculation-error',
    label: 'Rekenfout in oppervlakte-vermenigvuldiging',
    module: 'Metriek',
    description:
      'Leerling past de juiste oppervlakte-formule toe maar maakt een rekenfout in de vermenigvuldiging.',
    typicalFamilies: ['MET-OPP-VIERKANT', 'MET-OPP-RECHT'],
  },
  {
    id: 'met.area-side-confusion',
    label: 'Oppervlakte met zijde verward',
    module: 'Metriek',
    description:
      'Leerling geeft een zijde-lengte op als oppervlakte, of omgekeerd, bij terugrekenen.',
    typicalFamilies: ['MET-OPP-VIERKANT'],
  },
  {
    id: 'met.area-sqrt-error',
    label: 'Fout in worteltrekken (zijde uit oppervlakte)',
    module: 'Metriek',
    description:
      'Leerling maakt een fout bij worteltrekken om de zijde van een vierkant uit de oppervlakte te bepalen.',
    typicalFamilies: ['MET-OPP-VIERKANT'],
  },
  {
    id: 'met.area-divide-by-2',
    label: 'Door 2 delen i.p.v. wortel trekken',
    module: 'Metriek',
    description:
      'Leerling deelt de oppervlakte door 2 om de zijde van een vierkant te vinden i.p.v. de vierkantswortel te nemen.',
    typicalFamilies: ['MET-OPP-VIERKANT'],
  },

  // ─── Omtrek rechthoek/vierkant ──────────────────────────────────────────
  {
    id: 'met.omtrek-aantal-zijden-fout',
    label: 'Verkeerd aantal zijden bij omtrek',
    module: 'Metriek',
    description:
      'Leerling telt slechts één paar zijden op (l + b) of vergeet een zijde — krijgt bijvoorbeeld 16 i.p.v. 32 voor een 12×4 rechthoek.',
    typicalFamilies: ['MET-OMTREK-RECHT', 'MET-MEETK-OMTREK'],
  },
  {
    id: 'met.omtrek-terugrekenen-fout',
    label: 'Fout bij omtrek terugrekenen',
    module: 'Metriek',
    description:
      'Leerling deelt de gegeven omtrek niet eerst door 2 (of vergeet de lengte af te trekken) bij het terugrekenen van een ontbrekende zijde.',
    typicalFamilies: ['MET-OMTREK-RECHT', 'MET-OPP-RECHT'],
  },
  {
    id: 'met.omtrek-vierkant-deler-fout',
    label: 'Vierkant-omtrek door verkeerd getal gedeeld',
    module: 'Metriek',
    description:
      'Leerling deelt de omtrek van een vierkant door 2, 3 of 6 in plaats van door 4 om één zijde te krijgen — verwart het aantal zijden.',
    typicalFamilies: ['MET-OMTREK-RECHT'],
  },
  {
    id: 'met.opp-langste-zijde-aanname',
    label: 'Langste zijde = grootste oppervlakte aangenomen',
    module: 'Metriek',
    description:
      'Leerling kiest bij vergelijken de rechthoek met de langste zijde als grootste oppervlakte, zonder l × b uit te rekenen.',
    typicalFamilies: ['MET-OPP-RECHT'],
  },

  // ─── Oppervlakte-land (hectare/are/km²) ─────────────────────────────────
  {
    id: 'met.opp-hectare-100',
    label: '1 hectare als 100 m²',
    module: 'Metriek',
    description:
      'Leerling denkt dat 1 hectare gelijk is aan 100 m² (verwart hectare met are).',
    typicalFamilies: ['MET-OPP-LAND'],
  },
  {
    id: 'met.opp-are-10',
    label: '1 are als 10 m²',
    module: 'Metriek',
    description:
      'Leerling denkt dat 1 are gelijk is aan 10 m² i.p.v. 100 m² — onderschat de are met factor 10.',
    typicalFamilies: ['MET-OPP-LAND'],
  },
  {
    id: 'met.opp-are-1000',
    label: '1 are als 1000 m²',
    module: 'Metriek',
    description:
      'Leerling denkt dat 1 are gelijk is aan 1000 m² i.p.v. 100 m² — overschat de are met factor 10.',
    typicalFamilies: ['MET-OPP-LAND'],
  },
  {
    id: 'met.opp-are-gelijk-aan-m2',
    label: 'Are en m² als gelijk behandeld',
    module: 'Metriek',
    description:
      'Leerling behandelt are en m² alsof het dezelfde eenheid is, zonder omrekening.',
    typicalFamilies: ['MET-OPP-LAND'],
  },
  {
    id: 'met.opp-km2-ha-10',
    label: '1 km² als 10 hectare',
    module: 'Metriek',
    description:
      'Leerling denkt dat 1 km² gelijk is aan 10 hectare i.p.v. 100 hectare.',
    typicalFamilies: ['MET-OPP-LAND'],
  },
  {
    id: 'met.opp-km2-ha-1000',
    label: '1 km² als 1000 hectare',
    module: 'Metriek',
    description:
      'Leerling denkt dat 1 km² gelijk is aan 1000 hectare i.p.v. 100 hectare — overschat met factor 10.',
    typicalFamilies: ['MET-OPP-LAND'],
  },
  {
    id: 'met.opp-ha-gelijk-aan-km2',
    label: 'Hectare en km² als gelijk behandeld',
    module: 'Metriek',
    description:
      'Leerling behandelt hectare en km² alsof het dezelfde eenheid is, zonder omrekening tussen de twee.',
    typicalFamilies: ['MET-OPP-LAND'],
  },

  // ─── Kubieke inhoud — extra factor ──────────────────────────────────────
  {
    id: 'met.kubiek-x10000-i.p.v.-x1000',
    label: '×10.000 i.p.v. ×1.000 bij kubieke omrekening',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt een keer te veel bij kubieke omrekening (×10.000 i.p.v. ×1.000), bijv. door oppervlakte-regel met inhoud-regel te combineren.',
    typicalFamilies: ['MET-INH-OMREKEN-KUBIEK'],
  },

  // ─── Inhoud — metrieke trap (liter/centiliter/deciliter) ────────────────
  {
    id: 'met.inhoud-trap-1-trede-l-cl',
    label: 'l→cl als 1 trede (×10)',
    module: 'Metriek',
    description:
      'Leerling denkt dat van liter naar centiliter slechts 1 trede is (×10) i.p.v. 2 treden (×100).',
    typicalFamilies: ['MET-INH-CITER', 'MET-INH-LITER'],
  },
  {
    id: 'met.inhoud-trap-3-treden-l-cl',
    label: 'l→cl als 3 treden (×1000)',
    module: 'Metriek',
    description:
      'Leerling denkt dat van liter naar centiliter 3 treden is (×1000) i.p.v. 2 treden (×100) — verwart cl met ml.',
    typicalFamilies: ['MET-INH-CITER', 'MET-INH-LITER'],
  },
  {
    id: 'met.inhoud-trap-richting-fout-l-cl',
    label: 'l→cl richting omgekeerd',
    module: 'Metriek',
    description:
      'Leerling deelt waar vermenigvuldigd moet worden bij groot→klein (l→cl) — richting op de metrieke trap omgedraaid.',
    typicalFamilies: ['MET-INH-CITER', 'MET-INH-LITER'],
  },
  {
    id: 'met.inhoud-trap-1-trede-cl-l',
    label: 'cl→l als 1 trede (÷10)',
    module: 'Metriek',
    description:
      'Leerling denkt dat van centiliter naar liter slechts 1 trede is (÷10) i.p.v. 2 treden (÷100).',
    typicalFamilies: ['MET-INH-CITER'],
  },
  {
    id: 'met.inhoud-trap-3-treden-cl-l',
    label: 'cl→l als 3 treden (÷1000)',
    module: 'Metriek',
    description:
      'Leerling denkt dat van centiliter naar liter 3 treden is (÷1000) i.p.v. 2 treden (÷100).',
    typicalFamilies: ['MET-INH-CITER'],
  },
  {
    id: 'met.inhoud-trap-richting-fout-cl-l',
    label: 'cl→l richting omgekeerd',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt waar gedeeld moet worden bij klein→groot (cl→l) — richting op de metrieke trap omgedraaid.',
    typicalFamilies: ['MET-INH-CITER'],
  },
  {
    id: 'met.inhoud-trap-2-treden-dl-cl',
    label: 'dl→cl als 2 treden (×100)',
    module: 'Metriek',
    description:
      'Leerling denkt dat van deciliter naar centiliter 2 treden is (×100) i.p.v. 1 trede (×10).',
    typicalFamilies: ['MET-INH-CITER'],
  },
  {
    id: 'met.inhoud-dl-cl-gelijk',
    label: 'Deciliter en centiliter als gelijk',
    module: 'Metriek',
    description:
      'Leerling behandelt deciliter en centiliter alsof het dezelfde eenheid is, zonder factor 10 om te rekenen.',
    typicalFamilies: ['MET-INH-CITER'],
  },
  {
    id: 'met.inhoud-trap-richting-fout-dl-cl',
    label: 'dl→cl richting omgekeerd',
    module: 'Metriek',
    description:
      'Leerling deelt waar vermenigvuldigd moet worden bij groot→klein (dl→cl) — richting op de metrieke trap omgedraaid.',
    typicalFamilies: ['MET-INH-CITER'],
  },

  // ─── Cross-module: kommagetal-vermenigvuldiging in MET-context ──────────
  {
    id: 'met.decimal-multiplication-error',
    label: 'Rekenfout in komma-vermenigvuldiging',
    module: 'Metriek',
    description:
      'Leerling maakt een rekenfout bij vermenigvuldigen met kommagetallen in een metriek-context (bijv. parallellogram met decimale hoogte).',
    typicalFamilies: [
      'MET-OPP-PARALLELLOGRAM',
      'MET-OPP-DRIEHOEK',
      'MET-OPP-RECHT',
    ],
  },

  // ─── Samengestelde figuren (oppervlakte) ────────────────────────────────
  {
    id: 'met.samengesteld-deel-vergeten',
    label: 'Eén deel vergeten bij samengestelde figuur',
    module: 'Metriek',
    description:
      'Leerling berekent de oppervlakte van slechts één deel van een samengestelde figuur en vergeet de andere delen op te tellen.',
    typicalFamilies: ['MET-OPP-SAMENGESTELD'],
  },
  {
    id: 'met.samengesteld-buitenmaten-aannemen',
    label: 'Buitenmaten als volle rechthoek aangenomen',
    module: 'Metriek',
    description:
      'Leerling behandelt een samengestelde figuur (L-, T-, U-vorm) als een volle rechthoek met buitenmaten, zonder rekening te houden met openingen of inkepingen.',
    typicalFamilies: ['MET-OPP-SAMENGESTELD'],
  },
  {
    id: 'met.samengesteld-getallen-vermenigvuldigen',
    label: 'Alle getallen vermenigvuldigd i.p.v. delen optellen',
    module: 'Metriek',
    description:
      'Leerling vermenigvuldigt alle afmetingen door elkaar in plaats van elke deel-oppervlakte apart te berekenen en daarna op te tellen.',
    typicalFamilies: ['MET-OPP-SAMENGESTELD'],
  },
  {
    id: 'met.samengesteld-gat-niet-afgetrokken',
    label: 'Gat niet afgetrokken bij aftrekmethode',
    module: 'Metriek',
    description:
      'Leerling berekent het grote geheel maar vergeet de uitsparing (gat, schuurtje, opening) eraf te trekken bij een samengestelde figuur met uitsparing.',
    typicalFamilies: ['MET-OPP-SAMENGESTELD'],
  },
  {
    id: 'met.samengesteld-alleen-gat',
    label: 'Alleen het gat berekend',
    module: 'Metriek',
    description:
      'Leerling berekent alleen de oppervlakte van het gat/de uitsparing in plaats van het overgebleven gebied (geheel − uitsparing).',
    typicalFamilies: ['MET-OPP-SAMENGESTELD'],
  },
  {
    id: 'met.samengesteld-optellen-ipv-aftrekken',
    label: 'Gat opgeteld i.p.v. afgetrokken',
    module: 'Metriek',
    description:
      'Leerling telt de oppervlakte van het gat op bij het geheel in plaats van eraf te trekken bij een aftrek-methode-figuur.',
    typicalFamilies: ['MET-OPP-SAMENGESTELD'],
  },
]
